import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';
import { useCallback, useState } from 'react';
import { DocumentTitle } from 'components/DocumentTitle';
import { useTrack } from 'utils/hooks/useMixPanel';
import { RescheduleMixpanelEvents } from 'domains/direct-reschedule/mixpanel';
import { useNavigation } from 'utils/hooks/useNavigation';
import { isTimeSlotAvailable, useRescheduleState } from 'domains/direct-reschedule/useRescheduleProvider';
import { ReschedulePendingRescheduleDto, reschedulePendingReschedule } from 'domains/direct-reschedule/queries';
import { ApiError } from 'domains/api/errors';
import { useFlashMessages } from 'domains/flash-messages';
import { useDisplayTimezone } from 'domains/timezone/useDisplayTimezone';
import { RescheduleConfirmationDialog, RescheduleConfirmationResponse } from './RescheduleConfirmationDialog';
import {
  Button,
  FormLayout,
  Heading,
  LoadingSpinner,
  PageContainer,
  Paragraph,
  Reservation,
  ReservationCard,
  useDialog,
} from '@expressable/syntax-ds';
import { DATE_LONG_DAY_MONTH_YEAR, TIME_SHORT } from 'utils/date';

const RescheduleConfirmation = () => {
  useTrack(RescheduleMixpanelEvents.ViewedRescheduleConfirmation);

  const { dialogStateProps: RescheduleDirectConfirmDialogProps, onOpen: onOpenRescheduleDirectConfirmDialog } =
    useDialog();
  const { timeSlot, clientId, rescheduleId, rescheduleInformation } = useRescheduleState();
  const queryClient = useQueryClient();

  const { format, displayTimezone } = useDisplayTimezone();
  const { routes, navigate: sendTo } = useNavigation();

  const [modalStatus, setModalStatus] = useState<RescheduleConfirmationResponse | null>(null);

  const { t } = useTranslation();
  const { createFlash } = useFlashMessages();
  const navigate = useNavigate();

  const { mutateAsync: rescheduleAppointment, isLoading: isRescheduling } = useMutation<
    unknown,
    ApiError,
    ReschedulePendingRescheduleDto
  >(
    data => {
      if (!clientId || !rescheduleId) throw new Error('clientId or rescheduleId is undefined');
      const response = reschedulePendingReschedule(clientId, rescheduleId, data);

      return response;
    },
    {
      onSuccess: async () => {
        setModalStatus('success');
        onOpenRescheduleDirectConfirmDialog();
      },
      onError: async error => {
        // @ts-expect-error @TODO proper types here
        const code = error?.originalError?.response?.data?.code;

        switch (code) {
          case 'PENDING_RESCHEDULE_NOT_ACTIVE':
          case 'ACUITY_CALENDAR_NOT_AVAILABILITY':
            await queryClient.invalidateQueries('reschedule-available-times');
            setModalStatus('inactive');
            break;
          case 'PENDING_RESCHEDULE_NOT_FOUND':
            setModalStatus('notfound');
            break;
          default:
            // dev-note(gian): this is a catch-all error message for any other error that we don't know about
            sendTo(routes.root, {
              flash: createFlash('error', t('reschedule.confirm.genericError')),
            });
            return;
        }
        onOpenRescheduleDirectConfirmDialog();
      },
    },
  );

  const onConfirm = useCallback(() => {
    if (modalStatus !== 'success') navigate(-1);
    else sendTo(routes.root);
  }, [modalStatus]);

  if (!timeSlot || !displayTimezone) {
    sendTo(`/reschedule/${clientId}/${rescheduleId}`, {
      flash: createFlash('error', t('reschedule.confirm.invalidRescheduleState')),
    });
    return null;
  }

  const onSubmit = () => {
    // edge-case: if the user has been on the page for more than 2 hours from the selected timeslot, we don't allow them to reschedule
    if (!isTimeSlotAvailable(timeSlot)) {
      setModalStatus('inactive');
      onOpenRescheduleDirectConfirmDialog();

      return;
    }

    rescheduleAppointment({ dateTime: timeSlot.toISOString(), timeZone: displayTimezone?.resolvedName ?? '' });
  };

  return (
    <>
      <DocumentTitle>{t('reschedule.pageTitle')}</DocumentTitle>
      <PageContainer variant="form">
        <FormLayout>
          <FormLayout.Fields>
            <div>
              <Heading level="h1">{t('reschedule.confirm.mainHeading')}</Heading>

              <Paragraph noDefaultMargins size="lg" variant="primary">
                {t('reschedule.confirm.introText')}
              </Paragraph>
            </div>

            <ReservationCard title={t('reschedule.confirm.card.title')}>
              <Reservation
                ctaLabel={t('reschedule.confirm.card.cta')}
                dateLabel={`${format(timeSlot, DATE_LONG_DAY_MONTH_YEAR)} ${
                  rescheduleInformation?.therapistFirstName && rescheduleInformation?.therapistLastName
                    ? `with ${rescheduleInformation.therapistFirstName} ${rescheduleInformation.therapistLastName}`
                    : ''
                }`}
                onCtaClick={() => navigate(-1)}
                timeLabel={`${format(timeSlot, TIME_SHORT)} ${displayTimezone.label}`}
                title={`${rescheduleInformation?.originalAcuityAppointment?.type} (${rescheduleInformation?.originalAcuityAppointment?.duration}min)`}
              />
            </ReservationCard>
          </FormLayout.Fields>
        </FormLayout>

        <FormLayout.CTA>
          <Button onClick={onSubmit} size="lg" disabled={isRescheduling} variant="primary">
            {t('reschedule.confirm.submit')}
            {isRescheduling && <LoadingSpinner />}
          </Button>
        </FormLayout.CTA>

        <RescheduleConfirmationDialog
          {...RescheduleDirectConfirmDialogProps}
          status={modalStatus}
          onConfirm={onConfirm}
          isDismissible={false}
          isLoading={isRescheduling}
        />
      </PageContainer>
    </>
  );
};

export default RescheduleConfirmation;
